

import axios from "axios";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import InfluencerHeader from "./InfluencerHeader";
import Navbar from "./Navbar";
import { toast } from "react-toastify";

const Shop = () => {
  const influencerId = localStorage.getItem("influencerID");
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [deals, setDeals] = useState([]);
  const [generatedLinks, setGeneratedLinks] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState({});

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        "https://server-side-influencer.onrender.com/influencer/searchproduct",
        { searchTerm: search },
        { headers: { "Content-Type": "application/json" } }
      );
      const productList = res.data?.data?.products;
      setProducts(Array.isArray(productList) ? productList.slice(0, 12) : []);
    } catch (err) {
      console.error("Failed to fetch products", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const generateLink = async (productUrl, index) => {
    setLoadingLinks((prev) => ({ ...prev, [index]: true }));

    try {
      const res = await fetch(
        "https://server-side-influencer.onrender.com/influencer/generate-singlelink",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            originalUrl: productUrl.trim(),
            userId: influencerId,
          }),
        }
      );

      const data = await res.json();
      if (res.ok && data.shortLink) {
        setGeneratedLinks((prev) => ({ ...prev, [index]: data.shortLink }));
      } else {
        toast.error(data.error || "Failed to generate link");
      }
    } catch (err) {
      toast.error("Something went wrong while generating the link.");
    } finally {
      setLoadingLinks((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleCopy = (link, index) => {
    navigator.clipboard.writeText(link);
    setCopiedId(index);
    toast.success("Link copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  useEffect(() => {
    fetchProducts();
  }, [search]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 760);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const res = await axios.get(
          "https://server-side-influencer.onrender.com/influencer/productsdeals"
        );
        const dealsList = res.data?.data?.deals;
        if (Array.isArray(dealsList)) setDeals(dealsList.slice(0, 8));
      } catch (err) {
        console.error("Failed to fetch deals", err);
      }
    };
    fetchDeals();
  }, []);

  return (
    <div
      className="flex h-screen hide-scrollbar"
      style={{ marginLeft: isSmallScreen ? "0px" : "72px" }}
    >
      <div className="fixed top-0 left-0 right-0 z-40 bg-white shadow-md">
        <InfluencerHeader page="Shop" />
      </div>
      <div className="text-white fixed top-0 left-0 bottom-0 z-50">
        <Navbar />
      </div>

      <div className="flex-1 p-6 mt-12" style={{ marginLeft: isSmallScreen ? "0px" : "3px" }}>
        <div className="flex justify-center items-center gap-2 mb-6">
          <h2 className="text-xl font-extrabold text-gray-800 text-center mt-5 mb-4">
            Search Your Desired Product
          </h2>
        </div>

        <div className="flex justify-center mb-6">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-5 py-3 border border-gray-300 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(loading ? Array.from({ length: 6 }) : products).map((product, index) => (
            <div
              key={index}
              className="relative max-w-[600px] h-[480px] bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300"
            >
              <div
                className="h-1/2 w-full"
                style={{
                  height: isSmallScreen ? "250px" : "260px",
                  backgroundImage: loading
                    ? "none"
                    : `url(${product?.product_photo})`,
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {loading && <Skeleton height="100%" />}
              </div>

              <div className="p-4 pt-6 flex flex-col gap-2 h-1/2">
                <h3 className="text-lg font-bold text-gray-800 mt-4 text-center">
                  {loading ? (
                    <Skeleton width={120} />
                  ) : (
                    product?.product_title?.slice(0, 60) + "..."
                  )}
                </h3>
                <div className="flex justify-center items-center text-sm text-gray-600">
                  {loading ? (
                    <Skeleton width={80} />
                  ) : (
                    <>
                      <span className="text-lg font-semibold">{product?.product_price}</span>
                      {product?.product_original_price && (
                        <span className="line-through text-sm text-red-500 ml-2">
                          {product.product_original_price}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {!loading && (
                  <div className="flex justify-center mt-4">
                    {generatedLinks[index] ? (
                      <button
                        onClick={() => handleCopy(generatedLinks[index], index)}
                        className="text-sm bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition"
                      >
                        {copiedId === index ? "Copied!" : "Copy Link"}
                      </button>
                    ) : (
                      <button
                        onClick={() => generateLink(product.product_url, index)}
                        disabled={loadingLinks[index]}
                        className="text-sm bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition"
                      >
                        {loadingLinks[index] ? "Generating..." : "Get Link"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* --- Best Deals --- */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">🔥 Best Deals of the Day</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {deals.map((deal, index) => (
              <div
                key={index}
                className="relative max-w-[600px] h-[420px] bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 mt-10"
              >
                <div
                  className="h-1/2 w-full"
                  style={{
                    height: isSmallScreen ? "230px" : "230px",
                    backgroundImage: `url(${deal?.deal_photo})`,
                    backgroundSize: "100% 100%",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />

                <div className="p-4 pt-6 flex flex-col gap-2 h-1/2">
                  <h3 className="text-lg font-bold text-gray-800 mt-4 text-center">
                    {deal?.deal_title?.slice(0, 60) + "..."}
                  </h3>
                  <div className="flex justify-center items-center text-sm text-gray-600">
                    <span className="text-lg font-semibold">${deal?.deal_price?.amount}</span>
                    {deal?.list_price?.amount && (
                      <span className="line-through text-sm text-red-500 ml-2">
                        ${deal?.list_price?.amount}
                      </span>
                    )}
                  </div>
                  <a
                    href={`${deal.deal_url.split("?")[0]}?th=1&linkCode=ll1&tag=amanshop05c-20&linkId=71c4e0f315476905397ed6f77722b6af&language=en_US&ref_=as_li_ss_tl`}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-4 text-center text-white bg-blue-600 px-4 py-2 rounded-full hover:bg-blue-700"
                  >
                    View Deal
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
